import { baseApi } from './baseApi';
import { buildListQueryParams } from '../utils/pagination';
import { mapDailySaleRow, unwrapDailySale } from '../utils/dailySalesList';

function appendIds(params, key, ids) {
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    if (id == null || id === '') return;
    params.append(key, String(id));
  });
}

function asApiString(value) {
  return value == null ? '' : String(value).trim();
}

export function toCreateDailySaleBody(form = {}) {
  return {
    bill_no: asApiString(form.billNo),
    bill_type: asApiString(form.billType),
    location_name: asApiString(form.outlet),
    item_code: asApiString(form.itemCode),
    qty_sold: asApiString(form.quantity),
    tax_amount: asApiString(form.taxAmount),
    tax_rate: asApiString(form.taxRate),
    discount_amount: asApiString(form.discountAmount),
    gross_amount: asApiString(form.grossAmount),
    amount: asApiString(form.salesAmount),
    rate: asApiString(form.rate),
    unit_name: asApiString(form.unit),
  };
}

export function buildDailySalesQueryString(args = {}) {
  const params = new URLSearchParams();
  if (args.bill_date) params.set('bill_date', args.bill_date);
  params.set('page', String(args.page || 1));
  params.set('per_page', String(args.per_page || 2000));
  if (args.product_type_id != null && String(args.product_type_id).trim() !== '') {
    params.set('product_type_id', String(args.product_type_id));
  } else if (args.product_type) {
    params.set('product_type', args.product_type);
  }
  appendIds(params, 'product_category_ids[]', args.product_category_ids);
  appendIds(params, 'product_subcategory_ids[]', args.product_subcategory_ids);
  appendIds(params, 'product_ids[]', args.product_ids);
  return params.toString();
}

function saleDetailScore(payload) {
  const mapped = mapDailySaleRow(unwrapDailySale(payload) || {});
  let score = 0;
  if (mapped.items?.length) score += 10 + mapped.items.length;
  if (mapped.amount !== '' && mapped.amount != null) score += 3;
  if (mapped.createdAt) score += 2;
  if (mapped.guestCount !== '' && mapped.guestCount != null) score += 1;
  if (mapped.referenceNumber) score += 1;
  return score;
}

export const dailySalesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDailySales: builder.query({
      query: (args = {}) => {
        const qs = buildDailySalesQueryString(args);
        return qs ? `/api/daily_sales?${qs}` : '/api/daily_sales';
      },
      providesTags: ['DailySales'],
    }),
    getDailySalesList: builder.query({
      query: (args = {}) => ({
        url: '/api/daily_sales/sales_list',
        params: buildListQueryParams(args),
      }),
      providesTags: ['DailySales'],
    }),
    getDailySaleById: builder.query({
      async queryFn(id, _api, _extraOptions, baseQuery) {
        if (id == null || String(id).trim() === '') {
          return { error: { status: 400, data: { message: 'Missing daily sale id.' } } };
        }
        const urls = [`/api/daily_sales/${id}`, `/api/daily_sales/sales_list/${id}`];
        let best = null;
        let bestScore = -1;
        let lastError = null;

        for (const url of urls) {
          const result = await baseQuery(url);
          if (result.error) {
            lastError = result.error;
            continue;
          }
          const score = saleDetailScore(result.data);
          if (score > bestScore) {
            best = result.data;
            bestScore = score;
          }
          if (score >= 10) break;
        }

        if (best) return { data: best };
        return {
          error: lastError || {
            status: 404,
            data: { message: 'Daily sale not found.' },
          },
        };
      },
      providesTags: (_result, _error, id) => [{ type: 'DailySales', id }],
    }),
    importDailySalesCsv: builder.mutation({
      query: (file) => {
        const fd = new FormData();
        fd.append('file', file, file.name);
        return {
          url: '/api/daily_sales/import_csv',
          method: 'POST',
          body: fd,
        };
      },
      invalidatesTags: ['DailySales'],
    }),
    createDailySale: builder.mutation({
      query: (body) => ({
        url: '/api/daily_sales',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DailySales'],
    }),
    updateDailySale: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/daily_sales/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'DailySales',
        { type: 'DailySales', id },
      ],
    }),
  }),
});

export const {
  useGetDailySalesQuery,
  useGetDailySalesListQuery,
  useGetDailySaleByIdQuery,
  useImportDailySalesCsvMutation,
  useCreateDailySaleMutation,
  useUpdateDailySaleMutation,
} = dailySalesApi;
